import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import {
    HasProductContext,
    ProductContext,
    ProductContextModel,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import { Category } from "@insite/client-framework/Services/CategoryService";
import { BrandStateContext, getBrandStateById } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductStateByPath } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { BrandModel, CountryModel, ImageModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import FitImage from "@ucs/Components/Common/FitImage";
import ImageSelector from "@ucs/Components/ProductImageViewer";
import { isCategoryLvl1 } from "@ucs/Helpers/CategoryHelpers";
import PageBreadcrumbs, { THEMES } from "@ucs/Overrides/Components/PageBreadcrumbs";
import loadCurrentCountires from "@ucs/Store/Custom/LoadCurrentCountries";
import setCurrentCountries from "@ucs/Store/Custom/SetCurrentCountries";
import { getCurrentCountriesState, getIsLoadingCurrentCountriesState } from "@ucs/Store/CustomSelectors";
import { CustomState } from "@ucs/Store/Reducers";
import UCSColors from "@ucs/Utils/UCSColors";
import parse from "html-react-parser";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    title = "title",
    description = "description",
    focalPoint = "focalPoint",
    placeholderImage = "placeholderImage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
        [fields.focalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.placeholderImage]: string;
    };
}

const mapStateToProps = (state: CustomState, productContext?: ProductContextModel) => {
    let parentImages;
    if ((productContext as any)?.productContext?.product?.images?.length === 0) {
        parentImages = getProductStateByPath(
            state,
            `/Product/${(productContext as any)?.productContext.product.urlSegment}`,
        ).value?.images;
    }

    // This code will need to be refactored. This is a check to see if we're on a brand product list page. Currently, there's no way reliable to distinguish between a plp and a brand plp.
    let brandState = getBrandStateById(state, "");
    let isBrandsPLP = false;
    if (
        state.pages.productList?.parameter &&
        state.pages.productList.parameter.brandIds?.length === 1 &&
        getLocation(state).pathname.includes("Brands")
    ) {
        brandState = getBrandStateById(state, state.pages.productList.parameter.brandIds[0]);
        isBrandsPLP = true;
    }

    const {
        pages: {
            productList: { isSearchPage },
        },
    } = state;

    return {
        page: getCurrentPage(state),
        parentImages,
        currentCountries: getCurrentCountriesState(state),
        isLoadingCurrentCountries: getIsLoadingCurrentCountriesState(state),
        currentLocation: getLocation(state),
        isSearchPage,
        brandState,
        isBrandsPLP,
    };
};

const mapDispatchToProps = {
    loadCurrentCountires,
    setCurrentCountries,
};

const getBody = (node: React.ReactNode): React.ReactNode | false => {
    if (!node) return false;

    if (typeof node === "string" || typeof node === "number") {
        return node;
    }

    if (Array.isArray(node)) {
        for (const child of node) {
            const bodyContent = getBody(child);
            if (bodyContent) return bodyContent;
        }
        return false;
    }

    if (React.isValidElement(node)) {
        const element = node as React.ReactElement<{ children?: React.ReactNode }>;

        if (element.type === "body") {
            return element.props.children || false;
        }

        return getBody(element.props.children);
    }

    return false;
};

const enum BannerTypes {
    NORMAL,
    HALFWIDTH,
}

type Props = OwnProps &
    ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps> &
    HasProductContext;

const HeroBannerWrapper = styled.div`
    display: flex;

    ${({ theme }) =>
        breakpointMediaQueries(theme, [
            css`
                height: auto;
                flex-direction: column-reverse;
            `,
            css`
                min-height: 50vw;
                flex-direction: column-reverse;
            `,
            css`
                min-height: 45vw;
                flex-direction: column-reverse;
            `,
            css`
                min-height: 30vw;
            `,
            css`
                min-height: 30vw;
            `,
        ])};
`;

const HeroBannerTextSection = styled("div")<{ bannerType: BannerTypes }>`
    background-color: ${UCSColors.gray01};
    color: white;
    width: ${p => (p.bannerType === BannerTypes.HALFWIDTH ? "50%" : "380px")};
    font-size: 20px;
    padding: 3.75rem 30px;

    ${({ theme, bannerType }) =>
        breakpointMediaQueries(theme, [
            css`
                height: 100%;
                padding: 1.25rem 30px;
                width: 100%;
            `,
            css`
                width: ${bannerType === BannerTypes.HALFWIDTH ? "50%" : "100%"};
                padding: 1.25rem 30px;
            `,
            css`
                width: ${bannerType === BannerTypes.HALFWIDTH ? "50%" : "100%"};
                padding: 1.25rem 30px;
            `,
        ])};
    > h1 {
        ${({ theme }) =>
            breakpointMediaQueries(theme, [
                css`
                    margin-top: 0.5rem;
                    margin-bottom: 1rem;
                    font-size: 40px;
                `,
                css`
                    margin-top: 0.5rem;
                    margin-bottom: 1rem;
                    font-size: 40px;
                `,
                css`
                    margin-top: 0.5rem;
                    margin-bottom: 1.5rem;
                `,
                css`
                    margin-top: 0.5rem;
                    margin-bottom: 1.5rem;
                `,
                css`
                    margin-top: 0.5rem;
                    margin-bottom: 1.5rem;
                `,
            ])};
    }

    > p {
        margin-bottom: 0px;
        font-size: 20px;
        line-height: 24px;

        ${({ theme }) =>
            breakpointMediaQueries(theme, [
                css`
                    font-size: 18px;
                `,
                css`
                    font-size: 18px;
                `,
            ])};
    }
`;

const ImageSelectorStyles = {
    css: css`
        flex: 1;
        ${({ theme }) =>
            breakpointMediaQueries(theme, [
                css`
                    flex: none;
                    width: 100%;
                    height: 100%;
                    &:before {
                        padding-top: 100%;
                        content: "";
                        display: block;
                    }
                `,
            ])};
    `,
};

const ProductCode = styled.div`
    font-size: 12px;
    margin-bottom: 25px;
`;

const HeroBanner: React.FC<Props> = props => {
    const category: Category | undefined = React.useContext(CategoryContext);
    const product: ProductModel = React.useContext(ProductContext).product;
    const brand: BrandModel | undefined = React.useContext(BrandStateContext).value;
    const [bgImageState, changeImageState] = React.useState("");
    let title: string = props.fields.title || props.page.fields.title;
    let description: string | React.ReactNode | undefined = parse(props.fields.description || "");
    let bannerType: BannerTypes = BannerTypes.NORMAL;
    const [country, setCountry] = React.useState("");
    let useImageSelector = false;
    let footer = "";

    const imageWrapperStyles = {
        css: css`
            position: relative;
            overflow: hidden;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        flex: 1 1 auto;
                        height: 40vw;
                    `,
                    css`
                        width: 100%;
                        flex: 1 1 auto;
                        height: 40vw;
                    `,
                    css`
                        width: 100%;
                        flex: 1 1 auto;
                        height: 50vw;
                    `,
                    css`
                        width: calc(100% - 380px);
                    `,
                    css`
                        width: calc(100% - 380px);
                    `,
                ])};
        `,
    };

    React.useEffect(() => {
        if (props.currentCountries.length === 0 && !props.isLoadingCurrentCountries) {
            props.loadCurrentCountires({});
        } else {
            setCurrentCountry(props.currentCountries);
        }
    }, []);

    React.useEffect(() => {
        setCurrentCountry(props.currentCountries);
    }, [props.currentCountries]);

    React.useEffect(() => {
        changeImageState("");
        if (props.isSearchPage) {
            changeImageState(props.fields.placeholderImage);
        } else if (props.page.type === "CategoryDetailsPage" || props.page.type === "ProductListPage") {
            if (category) {
                changeImageState(category ? category.largeImagePath : "");
            } else if (props.isBrandsPLP) {
                if (props.brandState) {
                    changeImageState(props.brandState.value ? props.brandState.value.featuredImagePath : "");
                }
            }
        } else if (props.page.type === "ProductDetailsPage") {
            changeImageState(product ? product.largeImagePath : "");
        } else if (props.page.type === "BrandDetailsPage") {
            changeImageState(brand ? brand.featuredImagePath : "");
        } else if (props.page.type === "BrandsPage") {
            changeImageState(props.fields.placeholderImage);
        } else {
            changeImageState(props.fields.placeholderImage);
        }
    });

    const setCurrentCountry = (countries: CountryModel[]) => {
        countries.length !== 0 && setCountry(countries[0].abbreviation);
    };

    if (props.page.type === "CategoryDetailsPage" || props.page.type === "ProductListPage") {
        if (category) {
            if (props.page.type === "CategoryDetailsPage") {
                bannerType = BannerTypes.HALFWIDTH;
            } else if (props.page.type === "ProductListPage") {
                bannerType = BannerTypes.NORMAL;
            }
            if (!isCategoryLvl1(category.path)) {
                title = category.name;
                description = getBody(
                    parse(`<html><head><title></title></head><body>${category.htmlContent || ""}</body></html>`),
                );
            }
        }
    } else if (props.page.type === "ProductDetailsPage") {
        bannerType = BannerTypes.HALFWIDTH;
        // product = React.useContext(ProductContext).product;
        if (product) {
            title = product.productTitle;
            description = product.content?.metaDescription;

            // Search for locations
            if (product.attributeTypes) {
                if (country) {
                    product.attributeTypes.forEach(attributeType => {
                        // this.setState({ currentCountry: countries[0].abbreviation === "CA" ? Groups.Canada : Groups.USA });
                        if (
                            (attributeType.name.toLowerCase() === "location_canada" && country === "CA") ||
                            (attributeType.name.toLowerCase() === "location_us" && country !== "CA")
                        ) {
                            footer = "Stocked in ";
                            attributeType.attributeValues?.forEach((attributeValue, i) => {
                                // eslint-disable-next-line no-unsafe-optional-chaining
                                if (i === (attributeType.attributeValues as any)?.length - 1) {
                                    footer += " and ";
                                }

                                footer += attributeValue.value;

                                // eslint-disable-next-line no-unsafe-optional-chaining
                                if (i < (attributeType.attributeValues as any)?.length - 1) {
                                    footer += ", ";
                                } else {
                                    footer += ".";
                                }
                            });
                        }
                    });
                }
            }

            if (product.images && product.images.length > 0) {
                product.images.forEach((image: ImageModel) => {
                    if (
                        image.name.toLowerCase().includes("_swatch") ||
                        image.name.toLowerCase().includes("_alt1") ||
                        image.name.toLowerCase().includes("_alt2")
                    ) {
                        useImageSelector = true;
                    }
                });
            } else if (props.parentImages) {
                props.parentImages.forEach((image: ImageModel) => {
                    if (
                        image.name.toLowerCase().includes("_swatch") ||
                        image.name.toLowerCase().includes("_alt1") ||
                        image.name.toLowerCase().includes("_alt2")
                    ) {
                        useImageSelector = true;
                    }
                });
            }
        }
    } else if (props.page.type === "BrandsPage") {
        bannerType = BannerTypes.HALFWIDTH;
    } else if (props.page.type === "BrandDetailsPage") {
        bannerType = BannerTypes.HALFWIDTH;
        description = brand ? brand.metaDescription : "";
        title = brand ? brand.name : "";
    }

    return (
        <HeroBannerWrapper id="megaMenuContainer">
            <HeroBannerTextSection bannerType={bannerType}>
                <PageBreadcrumbs theme={THEMES.LIGHT}></PageBreadcrumbs>
                <Typography variant="h1" as="h1">
                    {title}
                </Typography>
                <Typography variant="p" as="p">
                    {description}
                </Typography>
                {footer && (
                    <Typography variant="p" as="p" size={16} style={{ marginTop: "25px", fontSize: "18px !important" }}>
                        {footer}
                    </Typography>
                )}
            </HeroBannerTextSection>
            {useImageSelector ? (
                <ImageSelector css={ImageSelectorStyles} product={product} />
            ) : (
                <FitImage
                    image={bgImageState}
                    focalPoint={props.fields.focalPoint}
                    containerStyles={imageWrapperStyles}
                />
            )}
        </HeroBannerWrapper>
    );
};

const definition: WidgetDefinition = {
    group: "Basic",
    displayName: "UCS Banner",
    fieldDefinitions: [
        {
            name: fields.title,
            displayName: "Title",
            defaultValue: "",
            editorTemplate: "TextField",
            fieldType: "General",
        },
        {
            name: fields.description,
            displayName: "Description",
            defaultValue: "",
            editorTemplate: "RichTextField",
            fieldType: "General",
        },
        {
            fieldType: "General",
            name: fields.focalPoint,
            displayName: "Focal Point",
            editorTemplate: "DropDownField",
            options: [
                { displayName: "Top Left", value: "topLeft" },
                { displayName: "Top Center", value: "topCenter" },
                { displayName: "Top Right", value: "topRight" },
                { displayName: "Center Left", value: "centerLeft" },
                { displayName: "Center", value: "center" },
                { displayName: "Center Right", value: "centerRight" },
                { displayName: "Bottom Left", value: "bottomLeft" },
                { displayName: "Bottom Center", value: "bottomCenter" },
                { displayName: "Bottom Right", value: "bottomRight" },
            ],
            defaultValue: "center",
            hideEmptyOption: true,
        },
        {
            name: fields.placeholderImage,
            tooltip:
                "The placeholder image will only be shown in these contexts: Brands Landing Page, Search Results and Content Pages",
            displayName: "Placeholder Image",
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            fieldType: "General",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: withProductContext(connect(mapStateToProps, mapDispatchToProps)(HeroBanner)),
    definition,
};

export default widgetModule;
